import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import MainHeader from './MainHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  Home,
  Users,
  Calendar,
  Award,
  Gift,
  Ticket,
  FileText,
  HelpCircle,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Code2,
  BookOpen,
  MapPin,
  BarChart3,
  ClipboardList,
  ClipboardCheck,
  Trophy,
  Coins,
  Star,
  UserPlus,
  Rocket,
  FileSpreadsheet,
  UserCheck,
  Clock,
  GraduationCap,
  Medal,
  Mail,
  Building2,
  Bot,
  Sparkles,
  Brain,
  Camera,
  School,
  Package,
  Truck,
  Newspaper,
  Globe,
  LineChart,
  Plug,
  CalendarClock,
  Shield,
  ShieldCheck,
  Briefcase,
  Heart,
  MessageSquare,
  Tent,
  Tag,
  Database,
} from 'lucide-react';

const parentNavItems = [
  { href: '/parent', label: 'Dashboard', icon: Home },
  { href: '/parent/stem-score', label: 'STEM Score', icon: Rocket },
  { href: '/parent/class-feed', label: 'Class Feed', icon: Camera },
  { href: '/parent/learners', label: 'My Learners', icon: Users },
  { href: '/parent/progress', label: 'Progress', icon: GraduationCap },
  { href: '/parent/enrollments', label: 'Enrollments', icon: Calendar },
  { href: '/parent/purchases', label: 'Purchase History', icon: FileText },
  { href: '/parent/recommendations', label: 'AI Recommendations', icon: Sparkles },
  { href: '/parent/points', label: 'Points', icon: Coins },
  { href: '/parent/rewards', label: 'Rewards Store', icon: Gift },
  { href: '/parent/raffles', label: 'Raffles', icon: Ticket },
  { href: '/parent/badges', label: 'Badges', icon: Award },
  { href: '/parent/certificates', label: 'Certificates', icon: ShieldCheck },
  { href: '/parent/reviews', label: 'Reviews', icon: MessageSquare },
  { href: '/parent/messages', label: 'Messages', icon: Mail },
  { href: '/parent/weekly-reports', label: 'AI Reports', icon: Brain },
  { href: '/parent/referral', label: 'Refer Friends', icon: UserPlus },
  { href: '/parent/forms', label: 'Forms & Waivers', icon: FileText },
];

const instructorNavItems = [
  { href: '/instructor', label: 'Dashboard', icon: Home },
  { href: '/instructor/roster', label: 'Roster', icon: Users },
  { href: '/instructor/attendance', label: 'Attendance', icon: Calendar },
  { href: '/instructor/assignments', label: 'Assignments', icon: ClipboardList },
  { href: '/instructor/checklists', label: 'Checklists', icon: ClipboardCheck },
  { href: '/instructor/completions', label: 'Completions', icon: Award },
  { href: '/instructor/messages', label: 'Messages', icon: Mail },
];

// Admin navigation — grouped hybrid structure.
// "Dashboard" and "Camps" stay flat (always visible, one-click access since
// they're used most). Everything else is grouped under collapsible sections
// so the sidebar stays short. Non-camp legacy items live under "More" at the
// bottom and remain reachable via direct URL regardless of collapse state.
const adminNavItems = [
  { href: '/admin', label: 'Dashboard', icon: Home },

  // Hottest group — always expanded for summer-camp focus mode.
  {
    isGroup: true, label: 'Camps', icon: Tent, defaultExpanded: true,
    children: [
      { href: '/admin/camps', label: 'All Camps', icon: Tent },
      { href: '/admin/camps/schedule', label: 'Schedule Matrix', icon: Calendar },
      { href: '/admin/enrollments', label: 'Enrollments', icon: Users },
      { href: '/admin/coupons', label: 'Coupons', icon: Tag },
    ],
  },

  {
    isGroup: true, label: 'People', icon: Users,
    children: [
      { href: '/admin/people', label: 'Parents & Learners', icon: Users },
      { href: '/admin/staff/all', label: 'Staff & Instructors', icon: UserCheck },
    ],
  },

  {
    isGroup: true, label: 'Content', icon: BookOpen,
    children: [
      { href: '/admin/courses', label: 'Courses', icon: BookOpen },
      { href: '/admin/images', label: 'Site Images', icon: Camera },
      { href: '/admin/reviews', label: 'Reviews', icon: Star },
      { href: '/admin/blog', label: 'Blog', icon: Newspaper },
      { href: '/admin/website-settings', label: 'Pages & Site Settings', icon: Globe },
    ],
  },

  { href: '/admin/communications', label: 'Communications', icon: MessageSquare },

  {
    isGroup: true, label: 'Rewards', icon: Gift,
    children: [
      { href: '/admin/rewards', label: 'Rewards Hub', icon: Gift },
      { href: '/admin/stem-certificates', label: 'STEM Certificates', icon: ShieldCheck },
    ],
  },

  {
    isGroup: true, label: 'Insights', icon: BarChart3,
    children: [
      { href: '/admin/reports', label: 'Reports', icon: BarChart3 },
      { href: '/admin/analytics', label: 'Analytics', icon: LineChart },
      { href: '/admin/weekly-digest', label: 'Weekly Digest', icon: Mail },
      { href: '/admin/imported-data', label: 'Imported Data', icon: FileSpreadsheet },
      { href: '/admin/sawyer-data', label: 'Sawyer Data', icon: FileSpreadsheet },
      { href: '/admin/data-cleanup', label: 'Data Cleanup', icon: Shield },
    ],
  },

  {
    isGroup: true, label: 'System', icon: Settings,
    children: [
      { href: '/admin/forms', label: 'Forms & Waivers', icon: FileText },
      { href: '/admin/operations', label: 'Operations', icon: Package },
      { href: '/admin/tools', label: 'Tools', icon: Settings },
      { href: '/admin/integrations', label: 'Integrations', icon: Plug },
      { href: '/admin/database', label: 'Database Admin', icon: Database },
      { href: '/admin/ai', label: 'AI Assistant', icon: Bot },
      { href: '/admin/bugs', label: 'Bug Fixing', icon: ClipboardList },
      { href: '/admin/help', label: 'Help Guide', icon: HelpCircle },
    ],
  },

  // Non-camp items collapsed by default — still reachable from here or via URL.
  // Re-promote when the business expands beyond summer camps.
  {
    isGroup: true, label: 'More', icon: ChevronRight,
    children: [
      { href: '/admin/scheduling', label: 'Classes Scheduling', icon: Calendar },
      { href: '/admin/checklists', label: 'Checklists', icon: ClipboardCheck },
      { href: '/admin/locations', label: 'Locations', icon: MapPin },
      { href: '/admin/franchises', label: 'Franchises', icon: Globe },
      { href: '/admin/partners', label: 'Partners', icon: Building2 },
    ],
  },
];

const partnerNavItems = [
  { href: '/partner', label: 'Dashboard', icon: Home },
  { href: '/partner/reports', label: 'Reports', icon: BarChart3 },
];

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getNavItems = () => {
    switch (role) {
      case 'admin': return adminNavItems;
      case 'instructor': return instructorNavItems;
      case 'partner': return partnerNavItems;
      default: return parentNavItems;
    }
  };

  const navItems = getNavItems();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Hybrid nav: auto-expand any group flagged `defaultExpanded` (admin Camps hub),
    // plus Staff for backwards-compat with the parent dashboard.
    const init = { Staff: true };
    navItems.forEach(item => {
      if (item.isGroup && item.defaultExpanded) init[item.label] = true;
      // Auto-expand any group whose child matches the current URL so the
      // active link is always visible on direct navigation / refresh.
      if (item.isGroup && item.children?.some(c => location.pathname === c.href || location.pathname.startsWith(c.href + '/'))) {
        init[item.label] = true;
      }
    });
    return init;
  });
  const toggleGroup = (label) => setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const NavContent = ({ mobile = false }) => (
    <div 
      className="h-full relative"
      style={{
        overflowY: 'scroll',
        scrollbarWidth: 'thin',
        scrollbarColor: '#1e40af #e2e8f0'
      }}
    >
      <style>{`
        .nav-scroll::-webkit-scrollbar {
          width: 12px !important;
          display: block !important;
        }
        .nav-scroll::-webkit-scrollbar-track {
          background: #cbd5e1 !important;
        }
        .nav-scroll::-webkit-scrollbar-thumb {
          background: #1e40af !important;
          border-radius: 6px !important;
          border: 2px solid #cbd5e1 !important;
        }
      `}</style>
      <div className="space-y-1 py-4 pb-8 nav-scroll" style={{ minHeight: '100%' }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          
          // Expandable group
          if (item.isGroup) {
            const isExpanded = expandedGroups[item.label];
            const hasActiveChild = item.children?.some(c => location.pathname === c.href);
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 w-full text-left ${
                    hasActiveChild
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium flex-1">{item.label}</span>
                  <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-muted pl-2">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildActive = location.pathname === child.href;
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => mobile && setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                            isChildActive
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          <ChildIcon className="h-4 w-4" />
                          <span className="font-medium">{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Regular link
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => mobile && setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="h-5 w-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
        {/* Scroll indicator at bottom */}
        <div className="text-center py-4 text-xs text-muted-foreground border-t mt-4">
          ↓ Scroll for more ↓
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Main Website Header */}
      <MainHeader />
      
      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:w-64 lg:flex-col flex-shrink-0" role="navigation" aria-label="Dashboard navigation">
          <div className="flex flex-col flex-1 bg-card border-r border-border overflow-hidden">
            {/* Nav - Scrollable */}
            <nav 
              className="flex-1 px-2 py-2 sidebar-nav overflow-y-auto"
            >
              <div className="space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  
                  if (item.isGroup) {
                    const isExpanded = expandedGroups[item.label];
                    const hasActiveChild = item.children?.some(c => location.pathname === c.href);
                    return (
                      <div key={item.label}>
                        <button
                          onClick={() => toggleGroup(item.label)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm w-full text-left ${
                            hasActiveChild ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="font-medium truncate flex-1">{item.label}</span>
                          <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-muted pl-2">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              const isChildActive = location.pathname === child.href;
                              return (
                                <Link key={child.href} to={child.href}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-200 text-xs ${
                                    isChildActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <ChildIcon className="h-3.5 w-3.5 shrink-0" />
                                  <span className="font-medium truncate">{child.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="font-medium truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-3 px-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile Menu Header */}
          <header className="lg:hidden sticky top-0 z-40 glass border-b border-border/50">
            <div className="flex items-center justify-between h-14 px-4">
              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-sidebar-trigger">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0">
                  <div className="flex items-center gap-2 h-16 px-6 border-b border-border">
                    <div className="w-9 h-9 rounded-lg bg-rsc-yellow flex items-center justify-center">
                      <Rocket className="w-5 h-5 text-rsc-navy transform -rotate-45" />
                    </div>
                    <span className="font-heading font-bold text-lg text-rsc-navy">Ready Set STEM</span>
                  </div>
                  <nav className="px-3 py-4">
                    <NavContent mobile />
                  </nav>
                </SheetContent>
              </Sheet>

              {/* User avatar for mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-dropdown-mobile">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary text-sm font-bold">
                        {user?.first_name?.[0]}
                      </span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/" className="flex items-center gap-2">
                      <Home className="h-4 w-4" />
                      Back to Site
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive"
                    data-testid="logout-btn-mobile"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-4 md:p-6 lg:p-8 flex-1">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
